/**
 * 判断専用 AI (Jev) に聞く「問い」の定義と、返ってきた答えの解釈。
 *
 * この層はネットワークに触らない。問いの形と、答えをドメインの値に落とす規則だけを持つ。
 * 実際の HTTP 呼び出しは apps/api/src/lib/jev.ts にある。
 * こう分けておくと、提供元を差し替えてもドメインの判断規則は 1 か所のままになる。
 *
 * 設計の前提は 2 つ:
 *   1. **答えが来なくてもアプリは動く** — 判断は「外れても軽い」ものにだけ使う
 *   2. **確信度で自動適用を絞る** — 低いときは従来の決定的ロジックに戻す
 *
 * 詳細は docs/jev-ideas.md を参照。
 */
import { z } from "zod";
import { inferCategory } from "../suggestions/catalog.js";
/**
 * 選択肢の問いに対する答え。
 *
 * probabilities は選択肢ごとの確率、confidence はその分布の尖り具合 (0〜1)。
 * 外部から来る値なので、使う前に必ずこのスキーマで検証する。
 */
export const choiceAnswerSchema = z.object({
    type: z.literal("choice"),
    choice: z.string(),
    probabilities: z.record(z.string(), z.number()).optional(),
    confidence: z.number().min(0).max(1),
});
export const answerSetSchema = z.record(z.string(), choiceAnswerSchema);
// --- 支出カテゴリの推定 -----------------------------------------------------
/** 問いの識別子。リクエストとレスポンスで同じキーを使う */
export const CATEGORY_QUESTION_KEY = "category";
/**
 * Jev に選ばせるカテゴリ (7 択)。
 *
 * ExpenseCategory は 8 種類あるが、**"other" は選択肢に入れない**。
 * "other" は「どれでもない」ではなく「判断できなかった」を表す値なので、
 * モデルに選ばせるより、確率分布から得られる確信度で判定したほうが情報が多い。
 * 確信度が足りなければ、こちら側で "other" に落とす (resolveCategory)。
 */
export const JUDGED_CATEGORIES = [
    "food",
    "transport",
    "stay",
    "leisure",
    "shopping",
    "utility",
    "gift",
];
const JUDGED_CATEGORY_SET = new Set(JUDGED_CATEGORIES);
/**
 * 選択肢の説明。
 *
 * EXPENSE_CATEGORY_LABELS の短いラベル (「食事」「交通」) だけでは、
 * 「スタバ」「Uber」のような語をどこに寄せるべきかが伝わらない。
 * カタログ (EXPENSE_CATALOG) に載っている代表例を並べて、境界を具体的に示す。
 */
const CATEGORY_CRITERIA = {
    food: "食事や飲食に使ったお金。飲み会・居酒屋・ランチ・カフェ・焼肉・お酒・コンビニでの飲食など",
    transport: "移動に使ったお金。電車・タクシー・新幹線・飛行機・レンタカー・ガソリン・高速道路・駐車場など",
    stay: "宿泊に使ったお金。ホテル・旅館・民泊・コテージなど",
    leisure: "遊びや観光に使ったお金。入場料・カラオケ・映画・温泉・テーマパーク・スキー・ゴルフなど",
    shopping: "物を買ったお金。食材の買い出し・日用品・お土産・雑貨など (飲食そのものを除く)",
    utility: "暮らしを維持するための費用。家賃・光熱費・通信費・サブスクなど",
    gift: "人に贈るためのお金。プレゼント・ご祝儀・お祝いのケーキなど",
};
/** 支出名からカテゴリを選ばせる問い */
export const CATEGORY_QUESTION = {
    type: "choice",
    instructions: "これは割り勘アプリに登録された支出の名前です。この支出はどのカテゴリに当てはまりますか。",
    criteria: CATEGORY_CRITERIA,
};
export const CATEGORY_QUESTIONS = { [CATEGORY_QUESTION_KEY]: CATEGORY_QUESTION };
/**
 * 自動適用してよい確信度の下限。
 *
 * カテゴリは一覧のアイコンにしか使わない (ユーザーには選ばせない) ため、
 * 外しても害が小さい。とはいえ「誕生日ケーキは gift か food か」のように
 * 人によって割れるものは分布が割れて確信度が下がるので、そこで切る。
 * 下回ったときは従来どおり "other" (= その他アイコン) にする。
 */
export const CATEGORY_CONFIDENCE_THRESHOLD = 0.6;
/**
 * 問い合わせる価値があるか。
 *
 * 辞書 (inferCategory) で引けるならそちらが確実で、速くて、無料。
 * 引けなかったときだけ外部に聞く。
 */
export function needsCategoryJudgement(title) {
    return title.trim() !== "" && inferCategory(title) === "other";
}
/**
 * 支出名と Jev の答えからカテゴリを決める。
 *
 * 優先順位は 辞書 > 確信度の高い判断 > "other"。
 * answer が null (失敗・タイムアウト・機能無効) でも必ず値を返すので、
 * 呼び出し側に分岐は要らない。
 */
export function resolveCategory(title, answer) {
    const fromDictionary = inferCategory(title);
    if (fromDictionary !== "other")
        return fromDictionary;
    if (answer === null)
        return "other";
    // 定義していない選択肢は使わない (提供元の実装差や、経由するゲートウェイの事故に備える)
    if (!JUDGED_CATEGORY_SET.has(answer.choice))
        return "other";
    if (answer.confidence < CATEGORY_CONFIDENCE_THRESHOLD)
        return "other";
    return answer.choice;
}
//# sourceMappingURL=index.js.map